export interface User {
  id: string;
  email: string;
  name: string;
  groups: string[];
  attributes: Record<string, unknown>;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
}

export interface HealthCheck {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  service: string;
  version?: string;
}

export interface AuthContext {
  user: User;
  roles: string[];
  isAuthenticated: boolean;
}

// Cognito User Management Types
export type CognitoUserStatus =
  | 'UNCONFIRMED'
  | 'CONFIRMED'
  | 'ARCHIVED'
  | 'COMPROMISED'
  | 'UNKNOWN'
  | 'RESET_REQUIRED'
  | 'FORCE_CHANGE_PASSWORD'
  /** Created by an external identity provider (Entra SSO); can sign in. */
  | 'EXTERNAL_PROVIDER';

export interface CognitoUser {
  username: string;
  sub: string; // Cognito user sub (unique identifier)
  email: string;
  name: string;
  enabled: boolean;
  userStatus: CognitoUserStatus;
  createdAt: string;
  lastModifiedAt: string;
  attributes: Record<string, string>;
  isFederated: boolean; // True if user was created via external identity provider (e.g., Azure Entra ID)
}

// Note: CreateUserRequest and UpdateUserRequest types are now derived from Zod schemas
// in packages/api/src/validators/users.ts. Import from there for type-safe validation.
export interface CreateUserRequest {
  email: string;
  name: string;
}

export interface CreateUserResponse {
  user: CognitoUser;
  temporaryPassword?: string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  enabled?: boolean;
}

export interface ResetPasswordResponse {
  temporaryPassword: string;
}

export interface RegenerateTempPasswordResponse {
  /** One-time temporary password. Never persist or log this value. */
  temporaryPassword: string;
  /** Email the customer must use to sign in. */
  email: string;
  /** Cognito Username (typically a UUID) for cache invalidation. */
  userId: string;
}

export interface ListUsersRequest {
  limit?: number;
  paginationToken?: string;
}

export interface ListUsersResponse {
  users: CognitoUser[];
  paginationToken?: string;
}

// ---------------------------------------------------------------------------
// Wellspring's own access roles (the Wellspring-* groups in Wellspring's pool),
// as opposed to the Plus-pool customer users above.

/** Roles a SuperAdmin can assign. Viewer is implicit via metron-internal, so it is not assignable. */
export const ASSIGNABLE_WELLSPRING_ROLES = ['SuperAdmin', 'Admin', 'Support'] as const;
export type AssignableWellspringRole = typeof ASSIGNABLE_WELLSPRING_ROLES[number];

export interface WellspringUser {
  username: string;
  email: string;
  name: string;
  enabled: boolean;
  isFederated: boolean;
  /** Assigned Wellspring role, or null when the user has none (implicit Viewer). */
  role: AssignableWellspringRole | null;
}

export interface ListWellspringUsersResponse {
  users: WellspringUser[];
}

export interface SetWellspringRoleRequest {
  role: AssignableWellspringRole | null;
}
